import { forwardRef } from "react";
import { HolderOutlined } from "@ant-design/icons";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// --- Base asset tag shell ---

export const AssetTag = forwardRef<
  HTMLSpanElement,
  {
    children: React.ReactNode;
    className?: string;
    style?: React.CSSProperties;
    faded?: boolean;
    elevated?: boolean;
  } & React.HTMLAttributes<HTMLSpanElement>
>(({ children, className = "", style, faded, elevated, ...props }, ref) => (
  <span
    ref={ref}
    className={`inline-flex items-center select-none rounded ring-1 ring-primary-300 bg-white overflow-hidden leading-none text-xs ${elevated ? "shadow-lg" : "shadow-sm"} ${faded ? "opacity-40" : ""} ${className}`}
    style={style}
    {...props}
  >
    {children}
  </span>
));

AssetTag.displayName = "AssetTag";

export function AssetTagHandle({
  children,
  highlight,
}: {
  children: React.ReactNode;
  highlight?: boolean;
}) {
  return (
    <span
      className={`${highlight ? "bg-blue-500" : "bg-primary-500"} text-white text-[13px] font-bold px-2 py-1 transition-colors`}
    >
      {children}
    </span>
  );
}

export function AssetTagLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="px-2 py-1 font-mono text-[13px] text-gray-700 tracking-tight">
      {children}
    </span>
  );
}

export function AssetTagRemove({ onRemove }: { onRemove: () => void }) {
  return (
    <span
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => {
        e.stopPropagation();
        onRemove();
      }}
      className="px-1.5 py-1 cursor-pointer text-gray-400 hover:text-red-400 border-l border-primary-300 transition-colors"
    >
      ×
    </span>
  );
}

// --- Draggable tag (unmatched assets pool) ---

export function DraggableAssetTag({ id }: { id: string }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `asset-${id}`,
    data: { type: "asset", assetId: id },
  });

  return (
    <AssetTag
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className="cursor-grab"
      faded={isDragging}
      aria-label={`Drag asset ${id} to a socket row`}
      aria-roledescription="draggable asset"
    >
      <AssetTagHandle>
        <HolderOutlined />
      </AssetTagHandle>
      <AssetTagLabel>{id}</AssetTagLabel>
    </AssetTag>
  );
}

// --- Sortable asset tag (within a row) ---

export function SortableAssetTag({
  id,
  socketId,
  connectorIndex,
  highlight,
  onRemove,
}: {
  id: string;
  socketId: string;
  connectorIndex: number;
  highlight?: boolean;
  onRemove: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `sortable-${socketId}-${id}`,
    data: { type: "sortable-asset", socketId, assetId: id },
  });

  return (
    <AssetTag
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      className="cursor-grab"
      faded={isDragging}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      aria-label={`Asset ${id}, connector ${connectorIndex}. Drag to reorder.`}
      aria-roledescription="sortable asset"
    >
      <AssetTagHandle highlight={highlight}>#{connectorIndex}</AssetTagHandle>
      <AssetTagLabel>{id}</AssetTagLabel>
      <AssetTagRemove onRemove={onRemove} />
    </AssetTag>
  );
}

// --- Droppable wrapper ---

export function DroppableAssetCell({
  rowKey,
  children,
}: {
  rowKey: string;
  children: React.ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: `row-${rowKey}`,
    data: { type: "asset-target", socketId: rowKey },
  });

  return (
    <div
      ref={setNodeRef}
      className={`min-h-[32px] p-1 rounded-md transition-all border border-dashed ${
        isOver
          ? "bg-primary-500/[0.06] border-primary-500"
          : "border-transparent"
      }`}
      aria-label={`Drop zone for socket ${rowKey}`}
      aria-roledescription="drop target"
    >
      {children}
    </div>
  );
}
